import React, { FormEvent, useEffect, useState } from 'react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useAddSubChapter, useSubChapters } from '../../react-query/queries';
import { Button, EmptyState, Modal, Spinner } from '../../school_panel/components/ui';
import { NAME_MAX, fieldClass } from '../../school_panel/lib/fieldClass';
import { apiErrorText } from '../../school_panel/lib/apiError';
import { FaBookOpen } from '../../school_panel/components/ui/icons';

interface SubchapterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  chapterId: number | null;
  chapterName: string;
}

const SubchapterDialog: React.FC<SubchapterDialogProps> = ({ isOpen, onClose, chapterId, chapterName }) => {
  const { token } = useAuth();
  const { data: subchaptersData, refetch: refetchSubchaps, isFetching, isError } = useSubChapters(token, chapterId);
  const addSubchapter = useAddSubChapter();
  const [newSubchapter, setNewSubchapter] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && chapterId) refetchSubchaps();
    if (isOpen) { setError(null); setNewSubchapter(''); }
  }, [isOpen, chapterId, refetchSubchaps]);

  const name = newSubchapter.trim();
  const list: { id: number; name: string }[] = subchaptersData?.subchapters ?? [];
  const duplicate = !!name && list.some(sc => sc.name.trim().toLowerCase() === name.toLowerCase());

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name || !chapterId || duplicate) return;
    setError(null);
    try {
      await addSubchapter.mutateAsync({ toBeValidated: token, chapterId, name });
      setNewSubchapter('');
      refetchSubchaps();
    } catch (err) {
      setError(apiErrorText(err, 'Could not save the subchapter. Please try again.'));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="block min-w-0">
          <span className="block font-display text-[17px] font-bold text-ink">Subchapters</span>
          <span className="mt-0.5 block truncate text-sm font-normal text-muted" title={chapterName}>{chapterName}</span>
        </span>
      }
      footer={<Button variant="ghost" onClick={onClose}>Done</Button>}
    >
      <form onSubmit={onSubmit} className="mb-4 flex items-start gap-2">
        <div className="flex-1">
          <input
            className={fieldClass}
            value={newSubchapter}
            maxLength={NAME_MAX}
            onChange={e => setNewSubchapter(e.target.value)}
            placeholder="New subchapter name"
            aria-label="New subchapter name"
          />
          {duplicate && <p className="mt-1 text-xs text-danger">This chapter already has a subchapter with that name.</p>}
          {error && <p role="alert" className="mt-1 text-xs text-danger">{error}</p>}
        </div>
        <Button type="submit" loading={addSubchapter.isPending} disabled={!name || duplicate}>Add</Button>
      </form>

      <div className="max-h-[300px] overflow-y-auto pr-1">
        {isFetching ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : isError ? (
          <p role="alert" className="rounded-[12px] bg-danger/10 px-3 py-2 text-sm text-danger">
            Subchapters couldn't be loaded for this chapter.
          </p>
        ) : list.length ? (
          <ul className="divide-y divide-line rounded-[12px] border border-line">
            {list.map(sc => (
              <li key={sc.id} className="break-words px-4 py-2.5 text-sm text-ink">{sc.name}</li>
            ))}
          </ul>
        ) : (
          <EmptyState compact icon={<FaBookOpen size={18} strokeWidth={1.8} />} title="No subchapters yet" body="Add the first one above." />
        )}
      </div>
    </Modal>
  );
};

export default SubchapterDialog;
